/**
 * Shared logic for QuestionsList follow-up condition evaluation and sorting.
 * Used by QuestionsList.jsx and by unit tests.
 */

/**
 * Sort key for conditional follow-up questions so the first matching condition is chosen.
 * Order: < then <= then = then >= then >. For same operator, < / <= / = use value ascending; >= / > use value descending.
 * @param {object} q - Question object with optional operator and value
 * @returns {[number, number]} Sort key [opOrder, valueOrder]
 */
export function getConditionSortKey(q) {
  if (q?.operator == null || q?.value == null) return [0, 0];
  const op = q.operator;
  const v = Number(q.value);
  if (op === "<") return [1, v];
  if (op === "<=") return [2, v];
  if (op === "=") return [3, v];
  if (op === ">=") return [4, -v];
  if (op === ">") return [5, -v];
  return [6, 0];
}

/**
 * Sort only the leading run of conditional follow-ups. Do not sort past the next L/non-conditional question (multiple follow-up blocks).
 * @param {object[]} questionList - List of questions (may contain conditional T and then L/O)
 * @returns {object[]} New array with leading conditional block sorted, rest unchanged
 */
export function sortQuestionListByConditionBlock(questionList) {
  const conditionalEndIndex = questionList.findIndex(
    (q) => q?.operator == null && q?.value == null
  );
  const end = conditionalEndIndex === -1 ? questionList.length : conditionalEndIndex;
  const conditionalBlock = questionList.slice(0, end);
  const rest = questionList.slice(end);
  conditionalBlock.sort((a, b) => {
    const [a0, a1] = getConditionSortKey(a);
    const [b0, b1] = getConditionSortKey(b);
    return a0 !== b0 ? a0 - b0 : a1 - b1;
  });
  return [...conditionalBlock, ...rest];
}

/**
 * Check if answer_code satisfies the question's operator/value condition.
 * @param {string|number} answer_code - User's answer code
 * @param {string} operator - "<" | "<=" | "=" | ">" | ">="
 * @param {string|number} value - Threshold value
 * @returns {boolean}
 */
export function isConditionMet(answer_code, operator, value) {
  const a = Number(answer_code);
  const v = Number(value);
  switch (operator) {
    case "<":
      return a < v;
    case "<=":
      return a <= v;
    case "=":
      return a === v;
    case ">":
      return a > v;
    case ">=":
      return a >= v;
    default:
      return false;
  }
}

/**
 * From a list of questions (conditional follow-ups first, then maybe next L/O), find the first question that either
 * has no operator/value (show as next) or whose condition is met by answer_code. Used to show exactly one follow-up per L answer.
 * @param {object[]} questionList - List of questions (not mutated; a copy is used internally)
 * @param {string|number} answer_code - Last L answer code
 * @returns {{ nextQuestion: object | null, newQuestionList: object[] }}
 */
export function findNextValidQuestion(questionList, answer_code) {
  const list = Array.isArray(questionList) ? [...questionList] : questionList;
  const nextQuestion = list[0];
  if (!nextQuestion) {
    return { nextQuestion: null, newQuestionList: list };
  }
  list.shift();
  if (nextQuestion?.operator != null && nextQuestion?.value != null) {
    const met = isConditionMet(
      answer_code,
      nextQuestion.operator,
      nextQuestion.value
    );
    if (met) {
      return { newQuestionList: list, nextQuestion };
    }
    return findNextValidQuestion(list, answer_code);
  }
  return { nextQuestion, newQuestionList: list };
}
